// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * Schema catalog service - fetches global schemas from the API.
 *
 * Schema IDs are namespaced: "system.{name}" or "{app_id}.{name}"
 */

import type { NodeApiClient } from '@/app-shell/services'
import type {
  AnalysisOutputSchemaCatalogEntry,
  CreateSchemaRequest,
  SchemaRecord,
  SchemaCatalogEntryDetail,
  UpdateSchemaRequest,
} from '../types'

interface SchemaCatalogResponse {
  schemas: AnalysisOutputSchemaCatalogEntry[]
}

export class SchemaCatalogService {
  constructor(private readonly client: NodeApiClient) {}

  /**
   * Fetch global schema catalog entries (read-only endpoint).
   */
  async fetch(): Promise<AnalysisOutputSchemaCatalogEntry[]> {
    const { data } = await this.client.get<SchemaCatalogResponse>('/schemas')
    return data.schemas ?? []
  }

  /**
   * Fetch a single schema entry with full json_schema payload (read-only endpoint).
   */
  async fetchDetail(schemaId: string): Promise<SchemaCatalogEntryDetail> {
    const { data } = await this.client.get<SchemaCatalogEntryDetail>(`/schemas/${schemaId}`)
    return data
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Admin CRUD operations
  // ─────────────────────────────────────────────────────────────────────────────

  /**
   * List all schemas (admin).
   */
  async list(params?: { namespace?: string }): Promise<SchemaRecord[]> {
    const queryParams = new URLSearchParams()
    if (params?.namespace) queryParams.set('namespace', params.namespace)

    const query = queryParams.toString()
    const url = query ? `/admin/schemas?${query}` : '/admin/schemas'
    const { data } = await this.client.get<SchemaRecord[]>(url)
    return data
  }

  /**
   * List schemas available to a workspace (system + this workspace's app schemas).
   */
  async listForWorkspace(orgId: string, wsId: string): Promise<SchemaRecord[]> {
    const { data } = await this.client.get<SchemaRecord[]>(
      `/org/${orgId}/ws/${wsId}/schemas`,
    )
    return data
  }

  /**
   * Get a schema by ID (admin).
   */
  async get(schemaId: string): Promise<SchemaRecord> {
    const { data } = await this.client.get<SchemaRecord>(`/admin/schemas/${schemaId}`)
    return data
  }

  /**
   * Create a new schema (admin).
   */
  async create(request: CreateSchemaRequest): Promise<SchemaRecord> {
    const { data } = await this.client.post<SchemaRecord>('/admin/schemas', request)
    return data
  }

  /**
   * Update a schema (admin).
   */
  async update(schemaId: string, request: UpdateSchemaRequest): Promise<SchemaRecord> {
    const { data } = await this.client.put<SchemaRecord>(`/admin/schemas/${schemaId}`, request)
    return data
  }

  /**
   * Delete a schema (admin).
   */
  async delete(schemaId: string): Promise<void> {
    await this.client.delete(`/admin/schemas/${schemaId}`)
  }
}
